/**
 * Authentication context and provider for Microsoft Entra ID.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { loadAuthConfig, msalConfig, isAuthConfigured } from '../config/authConfig';

// Kontekst konfiguracji autentykacji
const AuthConfigContext = createContext({
  isConfigured: false,
  isLoading: true,
  error: null,
  config: null
});

// Hook do używania kontekstu konfiguracji
export const useAuthConfig = () => {
  const context = useContext(AuthConfigContext);
  if (!context) {
    throw new Error('useAuthConfig must be used within AuthConfigProvider');
  }
  return context;
};

// Provider konfiguracji autentykacji
export const AuthConfigProvider = ({ children }) => {
  const [isConfigured, setIsConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [msalInstance, setMsalInstance] = useState(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Załaduj konfigurację z backendu
        const authConfig = await loadAuthConfig();
        
        // Jeśli autoryzacja wyłączona - natychmiast zakończ
        if (authConfig && authConfig.configured === false) {
          console.log('🔓 Auth disabled - skipping MSAL initialization');
          setIsConfigured(false);
          setIsLoading(false);
          return;
        }
        
        if (authConfig && authConfig.configured) {
          setConfig(authConfig);
          setIsConfigured(true);

          // Utwórz instancję MSAL
          const instance = new PublicClientApplication(msalConfig);
          await instance.initialize();
          
          // KLUCZOWE: Obsłuż redirect callback PRZED ustawieniem instance
          // To zapobiega pętli redirect na Azure
          try {
            const response = await instance.handleRedirectPromise();
            if (response) {
              console.log('✅ Redirect handled successfully:', response.account?.username);
            }
          } catch (redirectError) {
            console.error('❌ Error handling redirect:', redirectError);
          }
          
          setMsalInstance(instance);
          console.log('Authentication configured successfully');
        } else {
          setIsConfigured(false);
          console.warn('Authentication not configured on backend');
        }
      } catch (err) {
        console.error('Failed to initialize authentication:', err);
        setError(err.message);
        setIsConfigured(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const value = {
    isConfigured,
    isLoading,
    error,
    config,
    msalInstance
  };

  return (
    <AuthConfigContext.Provider value={value}>
      {isConfigured && msalInstance ? (
        <MsalProvider instance={msalInstance}>
          {children}
        </MsalProvider>
      ) : (
        children
      )}
    </AuthConfigContext.Provider>
  );
};